// Main entry point for UI library

import { SessionsView } from './components/sessions-view';
import { ReportsView } from './components/reports-view';
import { UIOptions } from './types';


// Define the types directly here to avoid import issues
interface SessionState {
  label: string;
  status: 'ready' | 'running' | 'passed' | 'failed';
  stats?: {
    tests: number;
    passes: number;
    failures: number;
    pending: number;
  };
  timestamp?: Date;
  duration?: number;
}

interface DetailedReport {
  data: any;
  stats: {
    tests: number;
    passes: number;
    failures: number;
    pending: number;
    duration?: number;
    start?: string;
    end?: string;
  };
  timestamp: Date;
  sessionLabel: string;
  size?: number;
}

interface CombinedReport {
  generatedAt: string;
  summary: {
    totalTests: number;
    totalPasses: number;
    totalFailures: number;
    totalPending: number;
    totalSessions: number;
    successfulSessions: number;
    failedSessions: number;
  };
  sessions: Record<string, DetailedReport>;
  executionDuration?: number;
}

// Helper function to render with external UI library (always expects it to be available)
function renderWithExternalUI(containerId: string, reportData: any, options: any = {}): void {
  window.MochaDetailedReporterUI.renderTestReport(containerId, reportData, {
    expandFailures: true,
    showConsoleLogs: true,
    theme: 'light',
    ...options
  });
}

export class MultiSessionRunnerUI {
  private container: HTMLElement;
  private options: UIOptions;
  private activeSessionLabel: string | null = null;
  
  constructor(containerId: string, options: Partial<UIOptions> = {}) {
    const containerElement = document.getElementById(containerId);
    if (!containerElement) {
      throw new Error(`Container element with ID "${containerId}" not found`);
    }
    
    this.container = containerElement;
    this.options = {
      theme: 'light',
      expandFailures: true,
      showConsoleLogs: true,
      onSessionSelect: () => {},
      onRunSession: () => {},
      onRunAllSessions: () => {},
      renderSessionsView: true,
      ...options
    };
    
    // Initialize container with base structure
    this.initializeContainer();
  }
  
  private initializeContainer(): void {
    this.container.classList.add('msdr-container');
    if (this.options.theme) {
      this.container.classList.add(`msdr-theme-${this.options.theme}`);
    }
    
    this.container.innerHTML = `
      <div class="msdr-main">
        ${this.options.renderSessionsView ? '<div class="msdr-sidebar" id="msdr-sessions-container"></div>' : ''}
        <div class="msdr-content">
          <div class="msdr-content-header" id="msdr-content-header">
            <div class="msdr-panel-title">Test Results</div>
            <div class="msdr-panel-actions">
              <div class="msdr-button-group">
                <button class="msdr-button small" id="msdr-expand-all">Expand All</button>
                <button class="msdr-button small" id="msdr-collapse-all">Collapse All</button>
              </div>
              <div class="msdr-button-group">
                <button class="msdr-button small" id="msdr-show-failures">Show Failures</button>
                <button class="msdr-button small" id="msdr-show-all">Show All</button>
              </div>
            </div>
          </div>
          <div class="msdr-results" id="msdr-results-container"></div>
        </div>
      </div>
    `;
    
    // Set up event listeners for controls
    this.setupEventListeners();
  }
  
  private setupEventListeners(): void {
    const expandAllBtn = document.getElementById('msdr-expand-all');
    const collapseAllBtn = document.getElementById('msdr-collapse-all');
    const showFailuresBtn = document.getElementById('msdr-show-failures');
    const showAllBtn = document.getElementById('msdr-show-all');
    
    if (expandAllBtn) {
      expandAllBtn.addEventListener('click', () => this.expandAllSuites());
    }
    
    if (collapseAllBtn) {
      collapseAllBtn.addEventListener('click', () => this.collapseAllSuites());
    }
    
    if (showFailuresBtn) {
      showFailuresBtn.addEventListener('click', () => this.showOnlyFailures());
    }
    
    if (showAllBtn) {
      showAllBtn.addEventListener('click', () => this.showAllTests());
    }
  }
  
  public renderSessions(sessionStates: Record<string, SessionState>): void {
    if (!this.options.renderSessionsView) return;
    
    const sessionsContainer = document.getElementById('msdr-sessions-container');
    if (!sessionsContainer) return;
    
    // Clear and render sessions using vanilla JS
    SessionsView({
      sessionStates,
      activeSessionLabel: this.activeSessionLabel,
      onSessionSelect: (label) => {
        this.activeSessionLabel = label;
        if (this.options.onSessionSelect) {
          this.options.onSessionSelect(label);
        }
      }
    }, sessionsContainer);
  }
  
  public renderReport(report: DetailedReport): void {
    const resultsContainer = document.getElementById('msdr-results-container');
    if (!resultsContainer) return;
    
    // Validate report before proceeding
    if (!report) {
      console.error('renderReport called with null or undefined report');
      this.showEmptyState('No report data available');
      return;
    }
    
    if (!report.sessionLabel) {
      console.error('renderReport called with report missing sessionLabel:', report);
      this.showEmptyState('Invalid report data - missing session label');
      return;
    }
    
    if (!report.data) {
      console.error('renderReport called with report missing data:', report);
      this.showEmptyState(`Report for "${report.sessionLabel}" has no test data. Please run the session again.`);
      return;
    }
    
    // Update panel title
    const headerTitle = document.querySelector('#msdr-content-header .msdr-panel-title');
    if (headerTitle) {
      headerTitle.textContent = `${report.sessionLabel} Results`;
    }
    
    // Create unique container for external UI - use DOM methods instead of innerHTML
    const reportContainerId = `external-report-${report.sessionLabel.replace(/\s+/g, '-')}`;
    
    // Clear the container properly to avoid lit-html conflicts
    resultsContainer.textContent = '';
    
    // Create the report container element
    const reportDiv = document.createElement('div');
    reportDiv.id = reportContainerId;
    resultsContainer.appendChild(reportDiv);
    
    try {
      // Always use external UI library (no fallback)
      renderWithExternalUI(reportContainerId, report.data, {
        expandFailures: this.options.expandFailures,
        showConsoleLogs: this.options.showConsoleLogs,
        theme: this.options.theme
      });
      
      // Apply integration styling
      this.applyExternalUIIntegration(reportContainerId);
      
      // Auto-expand failures if needed
      if (this.options.expandFailures) {
        setTimeout(() => this.expandFailedTests(), 100);
      }
    } catch (error:any) {
      console.error('Error rendering report with external UI:', error);
      
      // Fallback to basic display on error
      reportDiv.innerHTML = `
        <div style="padding: 1rem; border: 1px solid #e74c3c; border-radius: 4px; background: #fdf2f2;">
          <h3 style="color: #e74c3c; margin-top: 0;">Error Rendering Report</h3>
          <p><strong>Session:</strong> ${report.sessionLabel}</p>
          <p><strong>Error:</strong> ${error.message}</p>
          <p><strong>Tests:</strong> ${report.stats?.tests || 0}</p>
          <p><strong>Passed:</strong> ${report.stats?.passes || 0}</p>
          <p><strong>Failed:</strong> ${report.stats?.failures || 0}</p>
          <details style="margin-top: 1rem;">
            <summary>Raw Report Data</summary>
            <pre style="background: #f8f9fa; padding: 1rem; border-radius: 4px; overflow: auto; max-height: 300px;">${JSON.stringify(report, null, 2)}</pre>
          </details>
        </div>
      `;
    }
  }

  private applyExternalUIIntegration(containerId: string): void {
    // Apply styling to ensure proper integration with external UI
    setTimeout(() => {
      const container = document.getElementById(containerId);
      if (!container) return;

      // Ensure text visibility in our theme
      const elements = container.querySelectorAll('.mdr-test-title, .mdr-suite-title, .mdr-test-details, .mdr-log, .mdr-console-item');
      elements.forEach(el => {
        (el as HTMLElement).style.color = 'var(--msdr-text-color, #333)';
      });

      // Apply theme-specific styles
      if (this.options.theme === 'dark') {
        container.querySelectorAll('.mdr-report, .mdr-suite, .mdr-test').forEach(el => {
          (el as HTMLElement).style.backgroundColor = 'var(--msdr-bg-secondary, #252525)';
          (el as HTMLElement).style.color = 'var(--msdr-text-color, #f0f0f0)';
        });
      }
    }, 50);
  }

  public renderCombinedReport(combinedReport: CombinedReport): void {
    const resultsContainer = document.getElementById('msdr-results-container');
    if (!resultsContainer) return;
    
    // Update panel title
    const headerTitle = document.querySelector('#msdr-content-header .msdr-panel-title');
    if (headerTitle) {
      headerTitle.textContent = 'Combined Test Report';
    }
    
    // Clear container properly
    resultsContainer.textContent = '';
    
    // Create summary container
    const summaryDiv = document.createElement('div');
    summaryDiv.className = 'msdr-summary';
    
    // Build the summary HTML
    summaryDiv.innerHTML = `
      <h3>📋 Combined Test Report</h3>
      <div class="msdr-summary-grid">
        <div class="msdr-summary-item">
          <div class="msdr-summary-value">${combinedReport.summary.totalSessions}</div>
          <div class="msdr-summary-label">Sessions</div>
        </div>
        <div class="msdr-summary-item">
          <div class="msdr-summary-value">${combinedReport.summary.totalTests}</div>
          <div class="msdr-summary-label">Tests</div>
        </div>
        <div class="msdr-summary-item success">
          <div class="msdr-summary-value">${combinedReport.summary.totalPasses}</div>
          <div class="msdr-summary-label">Passed</div>
        </div>
        <div class="msdr-summary-item failure">
          <div class="msdr-summary-value">${combinedReport.summary.totalFailures}</div>
          <div class="msdr-summary-label">Failed</div>
        </div>
        <div class="msdr-summary-item">
          <div class="msdr-summary-value">${combinedReport.summary.successfulSessions}</div>
          <div class="msdr-summary-label">Successful Sessions</div>
        </div>
        <div class="msdr-summary-item">
          <div class="msdr-summary-value">${combinedReport.summary.failedSessions}</div>
          <div class="msdr-summary-label">Failed Sessions</div>
        </div>
      </div>
      <div class="msdr-actions" style="margin-top: 1rem;">
        <button class="msdr-button" id="msdr-download-report">
          Download Report
        </button>
      </div>
    `;
    
    resultsContainer.appendChild(summaryDiv);
    
    // Set up download button
    const downloadBtn = document.getElementById('msdr-download-report');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => {
        this.downloadReport(combinedReport);
      });
    }
  }
  
  public showEmptyState(message: string = 'Select a session to view results'): void {
    const resultsContainer = document.getElementById('msdr-results-container');
    if (!resultsContainer) return;
    
    // Clear container properly
    resultsContainer.textContent = '';
    
    // Create empty state container
    const emptyStateDiv = document.createElement('div');
    emptyStateDiv.className = 'msdr-empty-state';
    emptyStateDiv.innerHTML = `
      <div class="msdr-icon">📊</div>
      <h3>No Results Available</h3>
      <p>${message}</p>
    `;
    
    resultsContainer.appendChild(emptyStateDiv);
  }
  
  public setTheme(theme: 'light' | 'dark'): void {
    this.container.classList.remove('msdr-theme-light', 'msdr-theme-dark');
    this.container.classList.add(`msdr-theme-${theme}`);
    this.options.theme = theme;
  }
  
  private expandAllSuites(): void {
    // Handle both our internal components and external UI
    document.querySelectorAll('.msdr-suite-content').forEach(el => {
      el.classList.add('expanded');
    });
    document.querySelectorAll('.msdr-toggle-icon').forEach(el => {
      el.classList.add('expanded');
    });
    
    // Also handle external UI library elements
    document.querySelectorAll('.mdr-suite, .mdr-test').forEach(el => {
      el.classList.add('mdr-expanded');
    });
  }
  
  private collapseAllSuites(): void {
    // Handle both our internal components and external UI
    document.querySelectorAll('.msdr-suite-content').forEach(el => {
      el.classList.remove('expanded');
    });
    document.querySelectorAll('.msdr-toggle-icon').forEach(el => {
      el.classList.remove('expanded');
    });
    
    // Also handle external UI library elements
    document.querySelectorAll('.mdr-suite, .mdr-test').forEach(el => {
      el.classList.remove('mdr-expanded');
    });
  }
  
  private expandFailedTests(): void {
    // Handle our internal components
    document.querySelectorAll('.msdr-test-failed').forEach(test => {
      const parent = test.closest('.msdr-suite-content');
      if (parent) {
        parent.classList.add('expanded');
      }
      
      const parentSuite = test.closest('.msdr-suite');
      if (parentSuite) {
        const toggleIcon = parentSuite.querySelector('.msdr-toggle-icon');
        if (toggleIcon) {
          toggleIcon.classList.add('expanded');
        }
      }
      
      const details = test.querySelector('.msdr-test-details');
      if (details) {
        details.classList.add('expanded');
      }
    });

    // Also handle external UI library failed tests
    document.querySelectorAll('.mdr-test-failed').forEach(test => {
      test.classList.add('mdr-expanded');
      
      // Find parent suite and expand it
      const parentSuite = test.closest('.mdr-suite');
      if (parentSuite) {
        parentSuite.classList.add('mdr-expanded');
      }
    });
  }
  
  private showOnlyFailures(): void {
    // Handle both our components and external UI
    document.querySelectorAll('.msdr-test').forEach(el => {
      if (el.classList.contains('msdr-test-failed')) {
        (el as HTMLElement).style.display = '';
      } else {
        (el as HTMLElement).style.display = 'none';
      }
    });
    
    // Also handle external UI library
    document.querySelectorAll('.mdr-test').forEach(el => {
      if (el.classList.contains('mdr-test-failed')) {
        (el as HTMLElement).style.display = '';
      } else {
        (el as HTMLElement).style.display = 'none';
      }
    });
  }
  
  private showAllTests(): void {
    // Handle both our components and external UI
    document.querySelectorAll('.msdr-test').forEach(el => {
      (el as HTMLElement).style.display = '';
    });
    
    // Also handle external UI library
    document.querySelectorAll('.mdr-test').forEach(el => {
      (el as HTMLElement).style.display = '';
    });
  }
  
  private downloadReport(report: CombinedReport): void {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `combined-test-report-${timestamp}.json`;
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Error downloading report');
    }
  }
}

export function createMultiSessionRunnerUI(
  containerId: string, 
  options: Partial<UIOptions> = {}
): MultiSessionRunnerUI {
  return new MultiSessionRunnerUI(containerId, options);
}

// Export components for advanced usage
export * from './components/sessions-view';
export * from './components/test-view';
export * from './components/suite-view';
export * from './types';
